import {
  savedClaimSchema,
  claimEditSaveSchema,
  bulkClaimType,
  type ClaimTariff,
  IndividualBulkClaimType,
  sharedClaimType,
  type BulkClaimEditInput,
  type ClaimQuery,
  IntakeItem,
  Invoice,
} from '@billing/validation';

import {
  Healthcard,
  Patient,
  Payor,
  Provider,
  Site,
  Submission,
  Debit,
  ClaimHistory,
  Credit,
  Remittance,
  User,
  Group,
  ActionResponse,
  ActionRequest,
} from 'interfaces';

export interface Claim {
  // Not Null
  id: string;
  version: number;
  siteID: string;
  createdAt: string;
  referringProvider: string | null;
  // Nullable
  createdBy?: string | null;
  batchID?: string | null;
  providerID: string;
  patientID: string;
  messageID: string | null;
  groupID: string | null;
  updatedAt?: string;
  claimNumber?: string;
  providerComments?: string;
  clerkComments?: string;
  internalComments?: string;
  internalCommentSummary?: { count: number; latestText: string } | null;
  providerNotes?: string;
  chiroService?: string;
  icd9?: string;
  interpretingRadiologist?: string;
  locationOfService?: string;
  optometristReason?: string;
  patientCount?: number;
  serviceLocationIndicator?: string;
  virtualPhysicianRegion?: string;
  thirdPartyLiability?: string;
  incorporated?: string;
  claimType?: string | null;
  claimMode?: string | null;
  formType?: string | null;
  corporationIndicator?: string | null;
  specialCircumstances?: string | null;
  serviceStart?: string;
  serviceEnd?: string;
  healthcardID?: string | null;
  overhead: number | string;
  payorID?: string;
  resubmit?: boolean;
  hospitalName: string | null;
  hospitalCode: string | null;
  facilityNumber: string | null;
  credit?: Credit[];
  intakeItemID?: string | null;
  status: string;

  // Many to One
  healthcard?: Healthcard | null;
  createdByUser?: User | null;
  site: Site;
  patient: Patient;
  provider: Provider;
  payor?: Payor;
  referringProviderObject?: Provider;
  group: Group | null;
  intakeItem?: IntakeItem | null;
  // One to Many
  debits?: Array<Debit>;
  claimHistories?: Array<ClaimHistory>;
  // Many to Many
  tariffs: Array<ClaimTariff>;
  submissions?: Array<Submission>;
  remittances?: Array<Remittance>;
  invoices?: Array<Invoice>;
  actionRequests?: Array<ActionRequest>;
  actionResponses?: Array<ActionResponse>;
  claimQueries?: Array<ClaimQuery>;
  claimQueryIDs?: string[];
  hasQueryResponse?: boolean;

  // Computed list metadata
  relatedClaimCount?: number;
}

export type CreateClaimRequest = Claim & {
  duplicateSourceClaimID?: string;
};

export type FormClaim = Omit<
  Claim,
  'id' | 'createdAt' | 'debits' | 'claimHistories' | 'submissions'
>;

export type EditClaimForm = Omit<Claim, 'submissions'>;
export type BulkEditClaimForm = BulkClaimEditInput;

export type CreateClaimForm = IndividualBulkClaimType;
export type BulkClaimForm = bulkClaimType;
export type IndividualBulkClaimForm = IndividualBulkClaimType;
export type SharedClaimForm = sharedClaimType;

export const isClaim = (obj: any): obj is Claim => {
  return savedClaimSchema.safeParse(obj).success;
};

export const isEditClaim = (obj: any): obj is Claim => {
  return claimEditSaveSchema.safeParse(obj).success;
};

export enum AdjustmentReason {
  notPayable = 'Claim Not Payable',
  duplicate = 'Duplicate Claim',
}
